import classNames from "classnames";
import type { CSSProperties, ReactNode } from "react";

import { useScopedGeneratorStore } from "../store";
import type { V2Item, V2LabelDef } from "../types";
import { getComponentAdapter } from "../utils/componentAdapters";
import { resolveLabel, resolveUnit } from "../utils/merge";
import { resolveItemWidth } from "../utils/styleUtils";
import { Button } from "./inputs/Button/Button";
import { CheckboxInput } from "./inputs/CheckboxInput/CheckboxInput";
import { DateInput } from "./inputs/DateInput/DateInput";
import { ListInput } from "./inputs/ListInput/ListInput";
import { Par } from "./inputs/Par/Par";
import { TextAreaInput } from "./inputs/TextAreaInput/TextAreaInput";
import { TextInput } from "./inputs/TextInput/TextInput";
import { TimeInput } from "./inputs/TimeInput/TimeInput";
import { UsersInput } from "./inputs/UsersInput/UsersInput";
import { YachtDataInput } from "./inputs/YachtDataInput/YachtDataInput";
import { LocationInput } from "./inputs/LocationInput/LocationInput";
import styles from "./Item.module.scss";
import { ItemLabel } from "./shared/ItemLabel/ItemLabel";
import { Unit } from "./shared/Unit/Unit";

interface ItemProps {
  item: V2Item;
}

interface ItemWrapperProps {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

interface ItemBodyProps {
  orientation: string;
  labelNode: ReactNode;
  fieldNode: ReactNode;
}

const fieldWrapperStyle: CSSProperties = {
  position: "relative",
  display: "flex",
  width: "100%",
};

const unitWrapperStyle: CSSProperties = {
  position: "absolute",
  right: 0,
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  height: "100%",
  border: "1px solid transparent",
};

/**
 * Render a single logbook item with label, input, and optional unit.
 * @param item - Item definition to render
 * @returns JSX element
 */
export function Item({ item }: ItemProps) {
  const inherited = (item as any)._inherited || {};
  const itemId = item.id ?? item.item;
  const adapter = getComponentAdapter(item.component);
  const rawValue =
    useScopedGeneratorStore(s => s.values[itemId]) ?? item.value ?? "";
  const value = adapter.getValue(rawValue);
  const setValue = useScopedGeneratorStore(s => s.setValue);
  const isGlobalReadonly =
    useScopedGeneratorStore(s => s.entryData?.isReadonly) ?? false;
  const dispatchRowAction = useScopedGeneratorStore(s => s.dispatchRowAction);

  // ── Resolve effective label ──────────────────────────────────────────────
  // For component="label" the label props may be on the item root directly.
  const labelFromItemRoot: V2LabelDef | undefined =
    item.component === "label"
      ? sanitizeLabelOverrides({
          text: item.text,
          textAlign: item.textAlign,
          color: item.color,
          bold: item.bold,
          italic: item.italic,
          underline: item.underline,
          align: item.align,
          padding: item.padding,
          orientation: item.orientation,
          style: item.style,
        })
      : undefined;

  const itemLabel: V2LabelDef | undefined =
    sanitizeLabelOverrides(item.label) ?? labelFromItemRoot;

  const resolvedLabel = resolveLabel(itemLabel, inherited.label);
  const resolvedUnit = resolveUnit(item.unit, inherited.unit);

  // ── Orientation ──────────────────────────────────────────────────────────
  const orientation = resolvedLabel.orientation ?? "left";
  const hasLabel = Boolean(resolvedLabel.text) && orientation !== "none";

  // ── Value ────────────────────────────────────────────────────────────────
  const handleChange = (val: unknown) => {
    adapter.setValue(next => setValue(itemId, adapter.format(next)), val);
  };

  // ── Text-input inherited styles ──────────────────────────────────────────
  const textStyle: CSSProperties = {
    textTransform:
      "var(--gen-text-transform, none)" as CSSProperties["textTransform"],
  };

  // ── Item outer width ─────────────────────────────────────────────────────
  const outerStyle: CSSProperties = {
    ...resolveItemWidth(item.width),
    ...(item.outline !== undefined && { border: item.outline }),
  };

  // ── Input node ───────────────────────────────────────────────────────────
  const inputNode = renderInput(
    item,
    itemId,
    value,
    textStyle,
    handleChange,
    isGlobalReadonly,
    dispatchRowAction,
    { hasUnit: Boolean(resolvedUnit?.text) },
  );

  // ── Wrap input with unit if present ───────────────────────────────────────────────────────
  const fieldNode = (
    <div style={fieldWrapperStyle}>
      {inputNode}
      {resolvedUnit?.text && (
        <div style={unitWrapperStyle}>
          <Unit unit={resolvedUnit} />
        </div>
      )}
    </div>
  );

  // ── Special case: component="label" renders as a styled display row ──────
  if (item.component === "label") {
    return (
      <ItemWrapper className={styles.labelDisplay} style={outerStyle}>
        <ItemLabel label={resolvedLabel} />
      </ItemWrapper>
    );
  }

  // ── Standard layouts ─────────────────────────────────────────────────────
  const labelNode = hasLabel ? <ItemLabel label={resolvedLabel} /> : null;

  return (
    <ItemWrapper
      className={getOrientationClass(orientation)}
      style={outerStyle}>
      <ItemBody
        orientation={orientation}
        labelNode={labelNode}
        fieldNode={fieldNode}
      />
    </ItemWrapper>
  );
}

function ItemWrapper({ className, style, children }: ItemWrapperProps) {
  return (
    <div className={classNames(styles.item, className)} style={style}>
      {children}
    </div>
  );
}

function getOrientationClass(orientation: string) {
  if (orientation === "top") {
    return styles.orientTop;
  }

  if (orientation === "bottom") {
    return styles.orientBottom;
  }

  if (orientation === "right") {
    return styles.orientRight;
  }

  return styles.orientLeft;
}

function ItemBody({ orientation, labelNode, fieldNode }: ItemBodyProps) {
  if (orientation === "top") {
    return (
      <>
        {labelNode && <div className={styles.labelRow}>{labelNode}</div>}
        <div className={styles.inputRow}>{fieldNode}</div>
      </>
    );
  }

  if (orientation === "bottom") {
    return (
      <>
        <div className={styles.inputRow}>{fieldNode}</div>
        {labelNode && <div className={styles.labelRow}>{labelNode}</div>}
      </>
    );
  }

  if (orientation === "right") {
    return (
      <>
        {fieldNode}
        {labelNode}
      </>
    );
  }

  return (
    <>
      {labelNode}
      {fieldNode}
    </>
  );
}

function sanitizeLabelOverrides(
  label: Partial<V2LabelDef> | undefined,
): V2LabelDef | undefined {
  if (!label) {
    return undefined;
  }

  const sanitized = Object.fromEntries(
    Object.entries(label).filter(([, value]) => value !== undefined),
  ) as V2LabelDef;

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

// ── Input dispatcher ────────────────────────────────────────────────────────

/**
 * Dispatch to appropriate input component based on item type.
 * @param item - Item definition
 * @param itemId - Generated item ID
 * @param value - Current input value
 * @param textStyle - Text styling
 * @param onChange - Change handler
 * @returns Input JSX element or null
 */
function renderInput(
  rawItem: V2Item,
  itemId: string,
  value: unknown,
  textStyle: CSSProperties,
  onChange: (val: unknown) => void,
  isGlobalReadonly: boolean,
  dispatchRowAction: (action: {
    type: string;
    itemId: string;
    item: any;
  }) => void,
  props: { hasUnit: boolean },
) {
  const { hasUnit } = props;
  const item = { ...rawItem, readonly: rawItem.readonly ?? isGlobalReadonly };
  switch (item.component) {
    case "button": {
      const functionType = item.function as string | undefined;
      const label =
        item.text ?? (value !== undefined ? String(value) : undefined);
      const handleClick = () => {
        const rowAction = {
          type: functionType,
          itemId,
          item,
        };
        if (functionType) {
          dispatchRowAction(rowAction as any);
        } else {
          const event = new CustomEvent("row-action", {
            detail: rowAction,
          });
          window.dispatchEvent(event);
        }
      };
      return (
        <Button
          id={itemId}
          value={label}
          label={label}
          onClick={handleClick}
          textStyle={textStyle}
          readonly={item.readonly}
          functionType={functionType}
        />
      );
    }
    case "text":
      return (
        <TextInput
          id={itemId}
          value={String(value)}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "textArea":
      return (
        <TextAreaInput
          id={itemId}
          value={String(value)}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "par":
      return <Par list={item.list ?? []} layout={item.layout} />;

    case "check":
      return (
        <CheckboxInput
          id={itemId}
          value={value === "true" || value === true}
          readonly={item.readonly}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "list":
      return (
        <ListInput
          id={itemId}
          value={String(value)}
          options={item.options ?? []}
          placeholder={item.placeholder}
          readonly={item.readonly}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "number":
      return (
        <TextInput
          id={itemId}
          inputType="number"
          value={String(value ?? "")}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "loclookup":
      return (
        <LocationInput
          id={itemId}
          value={value as any}
          placeholder={item.placeholder}
          readonly={item.readonly}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "date":
      return (
        <DateInput
          id={itemId}
          value={String(value ?? "")}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          withIcon={!hasUnit}
          onChange={onChange}
          forwardDate={item?.features?.forwardDating}
        />
      );

    case "time":
      return (
        <TimeInput
          id={itemId}
          value={String(value ?? "")}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          withIcon={!hasUnit}
          onChange={onChange}
        />
      );

    case "yachtData":
      return (
        <YachtDataInput
          id={itemId}
          value={String(value ?? "")}
          yachtProp={item.yachtProp}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "users":
      return (
        <UsersInput
          id={itemId}
          value={String(value ?? "")}
          roles={item.roles}
          placeholder={item.placeholder}
          readonly={item.readonly}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "datetime":
      return (
        <TextInput
          id={itemId}
          inputType="datetime-local"
          value={String(value ?? "")}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "gps":
      return (
        <TextInput
          id={itemId}
          value={String(value ?? "")}
          placeholder={item.placeholder ?? "Lat, Long"}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "signature":
      return (
        <TextAreaInput
          id={itemId}
          value={String(value ?? "")}
          placeholder={item.placeholder ?? "Signature"}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "yachtinfo":
      return (
        <TextAreaInput
          id={itemId}
          value={String(value ?? "")}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );

    case "divider":
      return <hr className={styles.divider} />;

    case "spacer":
      return <div className={styles.spacer} aria-hidden="true" />;

    case "label":
      // Handled above as a full-row label display
      return null;

    default:
      return (
        <TextInput
          id={itemId}
          value={String(value ?? "")}
          placeholder={item.placeholder}
          readonly={item.readonly}
          outline={item.outline}
          textStyle={textStyle}
          onChange={onChange}
        />
      );
  }
}
